<template>
  <v-row
    no-gutters
    class="justify-center align-center flex-nowrap"
    style="height: 100%"
    :class="{ 'print-wrapper': isQuickPrint }"
    ref="navbar-menus-outer"
  >
    <v-col cols="auto" class="flex-shrink-1">
      <v-row
        ref="navbar-menus"
        no-gutters
        :class="{ 'no-print': isQuickPrint }"
        class="flex-nowrap"
        :style="`overflow-x: ${currentMenusWidth ? 'auto' : 'hidden'}`"
      >
        <template v-for="(menu, idx) in currentMenus">
          <v-col v-if="menu.tools" :key="idx" cols="auto">
            <the-navbar-top-panel-menu :level="0" v-bind="menu" :path="menu.name" />
          </v-col>
          <v-col v-else-if="menu.component" :key="idx" cols="auto">
            <component :is="menu.component" v-bind="menu" />
          </v-col>
          <v-col v-else :key="idx" cols="auto">
            <the-navbar-top-panel-menu-button v-bind="menu" />
          </v-col>
        </template>
        <v-col v-if="currentMoreMenu.tools.length" cols="auto">
          <the-navbar-top-panel-menu :level="0" v-bind="currentMoreMenu" :path="currentMoreMenu.name" />
        </v-col>
      </v-row>
    </v-col>
    <v-spacer />
    <v-col cols="auto" class="tw:mr-1.5">
      <v-row no-gutters align="center" class="tw:gap-2">
        <v-col cols="auto" :class="{ 'no-print': isQuickPrint }" v-if="alarmsVisible">
          <alarms-button />
        </v-col>
        <v-col cols="auto" :class="{ 'no-print': isQuickPrint }" v-if="unassignedWatermetersVisible">
          <unassigned-watermeters-button />
        </v-col>
        <v-col cols="auto" :class="{ 'no-print': isQuickPrint }" v-if="quickCaseAddingVisible">
          <quick-case-adding />
        </v-col>
        <v-col cols="auto">
          <v-row no-gutters class="tw:gap-1.5">
            <template v-for="button of filteredTopPanelButton">
              <the-navbar-top-panel-info-button
                :key="button.name"
                v-bind="button"
                :badge-value="badgeValues[button.name]"
                :class="{ 'no-print': isQuickPrint }"
              />
            </template>
          </v-row>
        </v-col>
      </v-row>
    </v-col>
    <export-inter-cad-dialog :is-visible.sync="isIntercadDialogVisible" v-if="isIntercadDialogLoaded" />
    <modeling-options-dialog :is-visible.sync="isOptionsDialogVisible" v-if="isOptionsDialogLoaded" />
    <scada-points-dialog :is-visible.sync="isScadaDialogVisible" v-if="isScadaDialogLoaded" />
    <scada-manager-dialog :is-visible.sync="isScadaManagerVisible" v-if="isScadaManagerLoaded" />
    <quick-task :is-visible.sync="isTaskDialogVisible" v-if="isTaskDialogLoaded" :selected-features="taskData" />
    <water-balance-reports-dialog :is-visible.sync="isWaterBalanceDialogVisible" v-if="isWaterBalanceDialogLoaded" />
  </v-row>
</template>

<script>
let resizeObserver;
import { get, call } from 'vuex-pathify';

import menus from '@/mixins/menus';

import { monitoring } from '@/assets/js/rziData';

import AlarmsButton from '@/components/AlarmsButton';
import UnassignedWatermetersButton from '@/components/UnassignedWatermetersButton';
import DotsMenu from '@/components/DotsMenu';
import TheNavbarTopPanelMenu from '@/components/TheNavbarTopPanelMenu';
import TheNavbarTopPanelMenuButton from '@/components/TheNavbarTopPanelMenuButton';
import TheNavbarTopPanelInfoButton from '@/components/TheNavbarTopPanelInfoButton';
import NavbarMenuTool from '@/components/NavbarMenuTool';
import QuickCaseAdding from '@/components/QuickCaseAdding';

import { debounce } from 'lodash';

export default {
  name: 'TheNavbarTopPanel',
  mixins: [menus],
  components: {
    AlarmsButton,
    UnassignedWatermetersButton,
    ExportInterCadDialog: () => import('@/components/ExportInterCadDialog'),
    QuickTask: () => import('@/components/QuickTask'),
    ScadaPointsDialog: () => import('@/components/ScadaPointsDialog'),
    ModelingOptionsDialog: () => import('@/components/ModelingOptionsDialog'),
    ScadaManagerDialog: () => import('@/components/ScadaManagerDialog'),
    WaterBalanceReportsDialog: () => import('@/components/WaterBalanceReportsDialog'),
    TheNavbarTopPanelMenu,
    DotsMenu,
    TheNavbarTopPanelMenuButton,
    TheNavbarTopPanelInfoButton,
    NavbarMenuTool,
    QuickCaseAdding,
  },
  data: vm => ({
    isOptionsDialogLoaded: false,
    isOptionsDialogVisible: false,
    isScadaDialogLoaded: false,
    isScadaDialogVisible: false,
    isTaskDialogLoaded: false,
    isTaskDialogVisible: false,
    isIntercadDialogLoaded: false,
    isIntercadDialogVisible: false,
    isScadaManagerLoaded: false,
    isScadaManagerVisible: false,
    isUserSettingsDialogLoaded: false,
    isUserSettingsDialogVisible: false,
    isWaterBalanceDialogLoaded: false,
    isWaterBalanceDialogVisible: false,
    taskData: {},
    badgeValues: {
      tasks: '0',
      failures: '0',
    },
    currentMenus: [],
    currentMoreMenu: {
      name: 'more',
      verboseName: vm.$i18n.t('default.more'),
      tools: [],
      color: 'primary',
      icon: 'mdi-chevron-down',
      isAuthorized: true,
      isToolWorking: true,
      level: 0,
      notDestroyable: true,
    },
    currentMenusWidth: null,
  }),
  computed: {
    currentLayerId: get('layers/currentLayer@id'),
    scadaAlarmsAdminEnabled: get('admin/settingsValues@scada_alarms_module_enabled'),
    scadaAlarmsSuperAdminEnabled: get('admin/additionalModules@SCADA_ALARMS.enabled'),
    modulesPermissions: get('users/toolsPermissions'),
    failuresStatuses: get('admin/modulesMapping@failures.statuses'),
    failuresLayerId: get('admin/modulesMapping@failures.layer_id'),
    tasksLayerId: get('admin/modulesMapping@tasks.layer_id'),
    isAdmin: get('users/currentUser@is_admin'),
    currentUser: get('users/currentUser'),
    isQuickPrint: get('tools/toolStatus@isQuickPrintToolActive'),
    layers: get('layers/layers'),
    projects: get('layers/projects'),
    projectElements: get('layers/project@layers'),
    projectLayers() {
      return this.$_getFlatGroupsLayers(this.projectElements).layers;
    },
    unassignedWatermetersProjectId: get('admin/settingsValues@unassigned_watermeters_project'),
    unassignedWatermetersLayerId: get(
      'admin/modulesMapping@unassigned_watermeters.unassigned_watermeters_partitions_datasource.settings.layer_id'
    ),
    unassignedWatermetersAdminEnabled: get('admin/settingsValues@unassigned_watermeters_module_enabled'),
    unassignedWatermetersSuperAdminEnabled: get('admin/additionalModules@UNASSIGNED_WATERMETERS_MODULE.enabled'),
    unassignedWatermetersConfigured: get('admin/additionalModules@UNASSIGNED_WATERMETERS_MODULE.configured'),
    unassignedWatermetersPermissions() {
      return this.modulesPermissions['UNASSIGNED_WATERMETERS_MODULE']?.main_value > 0;
    },
    unassignedWatermetersVisible() {
      return (
        this.unassignedWatermetersSuperAdminEnabled &&
        this.unassignedWatermetersAdminEnabled &&
        this.unassignedWatermetersConfigured &&
        this.unassignedWatermetersPermissions &&
        this.projects[this.unassignedWatermetersProjectId] &&
        this.layers[this.unassignedWatermetersLayerId]
      );
    },
    scadaAlarmsPermissions() {
      return this.modulesPermissions['SCADA_ALARMS']?.main_value > 0;
    },
    alarmsVisible() {
      return this.scadaAlarmsSuperAdminEnabled && this.scadaAlarmsAdminEnabled && this.scadaAlarmsPermissions;
    },
    quickCaseAddingVisible() {
      return this.$store.get('admin/isPermitted', { module: 'CASES' });
    },
    theNavbarTopPanelInfoButtons() {
      const failuresStatusFilters = [this.failuresStatuses?.active, this.failuresStatuses?.new];
      return [
        {
          name: 'tasks',
          otherTableRoutes: ['task'],
          icon: 'mdi-account-hard-hat',
          enableModuleName: ['TASKS'],
          enableSettingName: ['tasks_module_enabled'],
          permissionPrefix: 'tasks',
          additionalTableFilterValues: { assigned_user: [this.currentUser.id] },
          layerId: this.tasksLayerId,
          filters: {
            $EQUAL: {
              'tasks_task.assigned_user': {
                value: this.currentUser.id,
              },
            },
          },
          zIndex: 2,
        },
        {
          name: 'failures',
          otherTableRoutes: ['failure', 'newFailure'],
          icon: 'mdi-wrench',
          enableModuleName: ['FAILURES'],
          enableSettingName: ['failures_module_enabled'],
          permissionPrefix: 'tasks',
          additionalTableFilterValues: { status: failuresStatusFilters },
          layerId: this.failuresLayerId,
          filters: {
            $IN: {
              'failures_failure.status': {
                value: failuresStatusFilters,
              },
            },
          },
          settingName: 'failures_dispatcher_enabled',
          zIndex: 1,
        },
      ];
    },
    filteredTopPanelButton() {
      return this.filterPanels(this.theNavbarTopPanelInfoButtons).filter(
        panel => !panel.settingName || this.settings[panel.settingName]
      );
    },
  },
  methods: {
    getFeatures: call('layers/getLayerFeatures'),
    async getBadgeValue({ layerId, filters, name }) {
      try {
        const r = await this.getFeatures({
          features_filter: filters,
          layer_id: layerId,
          params: {
            with_features: false,
            with_geometry: false,
            with_total_count: true,
            with_count: false,
          },
        });
        this.badgeValues[name] = r.data.data.total_features_count.toString();
      } catch (error) {
        this.badgeValues[name] = '0';
        console.log('  error', error);
      }
    },
    getBadgesValues() {
      this.filteredTopPanelButton.forEach(badge => this.getBadgeValue(badge));
    },
    refreshNavbarInfoButtons(buttonsNames = ['failures', 'tasks']) {
      this.filteredTopPanelButton
        .filter(button => buttonsNames.includes(button.name))
        .forEach(badge => this.getBadgeValue(badge));
    },
    openRziMonitoringTab() {
      this.$store.set('table/ADD_TABLE_TAB!', {
        value: `rziMonitoring`,
        name: 'Monitoring',
      });
    },
    checkIfCollapseMenus() {
      const navbarMenus = this.$refs['navbar-menus'];
      if (!navbarMenus) return;
      if (this.currentMenusWidth && this.$refs['navbar-menus-outer']?.clientWidth > this.currentMenusWidth) {
        this.currentMoreMenu.tools = [];
        this.currentMenus = [...this.filteredMenus];
        this.currentMenusWidth = null;
        this.$nextTick(this.checkIfCollapseMenus);
      } else if (navbarMenus.scrollWidth > navbarMenus.clientWidth) this.collapseMenus();
    },
    collapseMenus() {
      if (this.currentMenus.length === 0) return;
      this.currentMoreMenu.tools.unshift(this.currentMenus.pop());
      this.$nextTick(() => {
        const navbarMenus = this.$refs['navbar-menus'];
        if (navbarMenus.scrollWidth > navbarMenus.clientWidth) this.collapseMenus();
        else {
          this.currentMenusWidth = this.$refs['navbar-menus-outer']?.clientWidth;
        }
      });
    },
  },
  created() {
    this.getBadgesValues();
  },
  mounted() {
    this.$root.$on('modelingOptions-action', () => {
      this.isOptionsDialogLoaded = true;
      this.isOptionsDialogVisible = true;
    });
    this.$root.$on('rziMonitoring-action', () => {
      if (!this.currentLayerId) {
        let layerId =
          this.projectLayers.find(layer => layer.data_source_name === monitoring.mainArea.datasource)?.id ||
          Object.values(this.layers).find(layer => layer.data_source_name === monitoring.mainArea.datasource)?.id;
        this.$router.push({ name: 'layerTable', params: { lid: layerId }, query: this.$route.query });
        this.$root.$once('tableDataRowsFetched', () => {
          this.openRziMonitoringTab();
        });
      } else {
        this.openRziMonitoringTab();
      }
    });
    this.$root.$on('exportInterCAD-action', () => {
      this.isIntercadDialogLoaded = true;
      this.isIntercadDialogVisible = true;
    });
    this.$root.$on('smetPoints-action', () => {
      this.isScadaDialogLoaded = true;
      this.isScadaDialogVisible = true;
    });
    this.$root.$on('scadaManager-action', () => {
      this.isScadaManagerLoaded = true;
      this.isScadaManagerVisible = true;
    });
    this.$root.$on('openTaskDialog', value => {
      this.taskData = value;
      this.isTaskDialogLoaded = true;
      this.isTaskDialogVisible = true;
    });
    this.$root.$on('waterBalanceReports-action', () => {
      this.isWaterBalanceDialogLoaded = true;
      this.isWaterBalanceDialogVisible = true;
    });
    this.$root.$off('refreshNavbarInfoButtons');
    this.$root.$on('refreshNavbarInfoButtons', this.refreshNavbarInfoButtons);
    this.currentMenus = [...this.filteredMenus];
    this.checkIfCollapseMenus = debounce(this.checkIfCollapseMenus, 150);
    // Menus-components need to fully render and calculate their width
    setTimeout(() => {
      this.$nextTick(() => {
        resizeObserver = new ResizeObserver(this.checkIfCollapseMenus);
        resizeObserver.observe(this.$refs['navbar-menus-outer']);
      });
    }, 1000);
  },
  beforeDestroy() {
    resizeObserver?.disconnect();
  },
};
</script>
